import { Href, Link } from "expo-router";
import React, { ReactElement, memo } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { ReportsPlayerMatchFragment } from "@/generated/graphql";

/**
 * Props interface for Matches view component
 */
interface Props {
  intakeUrl: string;
  selectedMatchIds: string[];
  items: ReportsPlayerMatchFragment[];
  renderItem: ListRenderItem<ReportsPlayerMatchFragment>;
  refreshing: boolean;
  isDark: boolean;
  bottomInset: number;
  widthContent: number;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderListEmptyComponent: () => ReactElement | null;
}
const keyExtractor = (item: ReportsPlayerMatchFragment) => item?.matchId;

/**
 * Displays a scrollable list of player matches with selection controls for report generation.
 * Shows header with intake form navigation button and match selection count.
 * @param root0 - The component props
 * @param root0.intakeUrl - Pre-computed URL to the intake form with selected matches
 * @param root0.items - Array of match data to display in the list
 * @param root0.renderItem - Function to render each individual match item in the list
 * @param root0.refreshing - Boolean indicating whether the list is currently refreshing data
 * @param root0.isDark - Whether dark theme is active for RefreshControl colors
 * @param root0.bottomInset - Bottom safe area inset for proper content padding
 * @param root0.widthContent - Width value for the content container
 * @param root0.onLoadMore - Callback invoked when user scrolls near end to load more matches
 * @param root0.onRefresh - Callback invoked when user pulls down to refresh the match list
 * @param root0.renderListEmptyComponent - Function to render empty state UI when no matches exist
 * @param root0.selectedMatchIds - Array of currently selected match IDs for the intake form
 */
const MatchesView = ({
  intakeUrl,
  items,
  renderItem,
  refreshing,
  isDark,
  bottomInset,
  widthContent,
  onLoadMore,
  onRefresh,
  renderListEmptyComponent,
  selectedMatchIds,
}: Props) => (
  <Stack className="flex-1 items-center pb-4">
    <FlashList
      contentContainerClassName="self-center flex-grow px-3"
      ListHeaderComponent={
        <Stack>
          <Link asChild href={intakeUrl as Href}>
            <Button
              label={`Go to Intake Form with ${selectedMatchIds.length} matches selected`}
            />
          </Link>
        </Stack>
      }
      data={items}
      scrollEventThrottle={16}
      refreshing={refreshing}
      contentContainerStyle={{
        width: widthContent,
        paddingBottom: bottomInset,
      }}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      ListEmptyComponent={renderListEmptyComponent}
      onEndReached={onLoadMore}
      onRefresh={onRefresh}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={[isDark ? "#ffffff" : "#000000"]}
          tintColor={isDark ? "#ffffff" : "#000000"}
        />
      }
      ItemSeparatorComponent={ItemSeparator}
    />
  </Stack>
);

MatchesView.displayName = "MatchesView";

export default memo(MatchesView);
